
import { useState, useEffect } from "react"

import { useDispatch } from "react-redux"

import { Grid, List, ListItem, ListItemText, Typography, Box, Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Select, MenuItem, FormControl, InputLabel, IconButton, TextField, CircularProgress } from "@mui/material"

import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

import moment from "moment"

import { formatCurrency } from "../helpers/FormatCurrency";

import { InfoField } from "./InfoField";

import { DialogDocumento } from "../dialogs/DialogDocumento";
import { DialogPartesExhortoRecibido } from "../dialogs/DialogPartesExhortoRecibido";

import { DialogContentStyle, DialogActionsStyle } from "../styles/DialogStyle";

import { openSnackbar } from "../store/slices/SnackbarSlice"

import { ObtenerTipoJuicio } from "../connections/catalogos/TipoJuicioConnection";
import { EditarExhortoRecibido } from "../connections/recepcion/ExhortoRecibidoConnection";

import { AcuerdoProps } from "../interfaces/tribunal/AcuerdoInterface";
import { TipoJuicioProps } from "../interfaces/catalogos/TipoJuicioInterface";
import { ExhortoRecibidoProps } from "../interfaces/recepcion/ExhortoRecibidoInterface";

interface Props {
    exhortoRecibido?:   ExhortoRecibidoProps;
    esPromocion?:       boolean;
    edit?:              boolean;
    setAcuerdo?:        React.Dispatch<React.SetStateAction<AcuerdoProps>>;
}

export const BoxInformacionExhortoRecibido = ( { exhortoRecibido, esPromocion = false, edit = false, setAcuerdo }: Props ) => {

    const dispatch = useDispatch();

    const [openAnexos, setOpenAnexos] = useState( false );
    const [selectedAnexoUrl, setSelectedAnexoUrl] = useState('');
    const [openDocumento, setOpenDocumento] = useState( false );

    const [openDialogPartes, setOpenDialogPartes] = useState( false );

    const [tipoJuicio, setTipoJuicio] = useState( 0 );
    const [tipoJuicioArray, setTipoJuicioArray] = useState<TipoJuicioProps[]>( [] );

    const [cuantia, setCuantia] = useState( exhortoRecibido?.cuantia ?? '' );

    const [actores, setActores] = useState<string[]>( exhortoRecibido?.actores ?? [] );
    const [demandados, setDemandados] = useState<string[]>( exhortoRecibido?.demandados ?? [] );

    const [loadingTipoJuicio, setLoadingTipoJuicio] = useState( false );
    const [loadingCuantia, setLoadingCuantia] = useState( false );

    const handleGuardarExhorto = async ( campo: 'tipoJuicio' | 'cuantia' ) => {

        const setLoading = campo === 'tipoJuicio' ? setLoadingTipoJuicio : setLoadingCuantia;

        setLoading( true );

        await EditarExhortoRecibido({ idExhortoRecibido: exhortoRecibido?.idExhortoRecibido ?? 0, idTipoJuicio: tipoJuicio, cuantia }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    const tipoJuicioDesc = tipoJuicioArray.find( (t) => t.idTipoJuicio === tipoJuicio )?.descripcion ?? '';

                    dispatch( openSnackbar({ message, variant: 'success' }) );

                    setAcuerdo?.( prev => ({
                        ...prev,
                        exhortoRecibido: {
                            ...prev.exhortoRecibido,
                            idTipoJuicio: tipoJuicio,
                            tipoJuicio:   tipoJuicioDesc,
                            cuantia,
                        }
                    }));

                } else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                }

                setLoading( false );

            }, 700);

        });

    }

    const handleCloseAnexos = () => {

        setOpenAnexos( false );
        setSelectedAnexoUrl( '' );

    }

    useEffect(() => {

        async function Obtener() {

            await ObtenerTipoJuicio().then( resp => {
                setTipoJuicioArray( resp );
            });

        }

        if( edit ){
            Obtener();
        }

    }, [ edit ]);

    useEffect(() => {

        if( tipoJuicioArray.length !== 0 ){
            setTipoJuicio( exhortoRecibido?.idTipoJuicio ?? 0 );
        }

    }, [ exhortoRecibido?.idTipoJuicio, tipoJuicioArray.length ]);

    useEffect(() => {

        setCuantia( exhortoRecibido?.cuantia ?? '' );

    }, [ exhortoRecibido?.cuantia ]);

    useEffect(() => {

        setActores( exhortoRecibido?.actores ?? [] );
        setDemandados( exhortoRecibido?.demandados ?? [] );

    }, [ exhortoRecibido?.actores, exhortoRecibido?.demandados ]);

    return (

        <Box p={4} component={ Paper }>

            <Grid container spacing={3}>

                {
                    exhortoRecibido?.folioBuzonElectronico
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Folio buzón electrónico"
                                value={ exhortoRecibido?.folioBuzonElectronico }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    exhortoRecibido?.folioBuzonElectronico
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={ <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}> Anexo(s) buzón electrónico: </Typography> }
                                        secondary={
                                            <Button
                                                fullWidth
                                                variant='contained'
                                                sx={{ mt: 1 }}
                                                onClick={ () => setOpenAnexos(true) }
                                            >
                                                Anexo(s)
                                            </Button>
                                        }
                                    />

                                </ListItem>

                            </List>

                        </Grid>
                }

                {
                    exhortoRecibido?.numeroFolio
                    ?
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Número de exhorto"
                                value={
                                    <>
                                        { exhortoRecibido?.numeroExhorto }
                                        { exhortoRecibido?.numeroFolio && <small style={{ fontSize: 14 }}> (F. { exhortoRecibido?.numeroFolio })</small> }
                                    </>
                                }
                                fontSize={ 16 }
                            />

                        </Grid>
                    :
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Número de exhorto"
                                value={ exhortoRecibido?.numeroExhorto }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    exhortoRecibido?.numeroExpedienteOrigen
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Número de expediente de origen"
                                value={ exhortoRecibido?.numeroExpedienteOrigen }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    exhortoRecibido?.fecha
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Fecha"
                                value={ moment( new Date( exhortoRecibido?.fecha ) ).format( 'DD-MM-YYYY' ) }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( exhortoRecibido?.tipoJuicio || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            {
                                edit
                                ?
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                        <FormControl fullWidth>

                                            <InputLabel shrink>Tipo de juicio</InputLabel>

                                            <Select
                                                notched
                                                label="Tipo de juicio"
                                                value={ tipoJuicio }
                                                onChange={ (e) => setTipoJuicio( e.target.value as number ) }
                                            >
                                                <MenuItem value={0}>Selecciona una opción</MenuItem>

                                                {
                                                    tipoJuicioArray.map( ({ idTipoJuicio, descripcion }) => (
                                                        <MenuItem key={ idTipoJuicio } value={ idTipoJuicio }>{ descripcion }</MenuItem>
                                                    ))
                                                }

                                            </Select>

                                        </FormControl>

                                        <IconButton
                                            color='primary'
                                            onClick={ () => handleGuardarExhorto( 'tipoJuicio' ) }
                                            disabled={ loadingTipoJuicio || tipoJuicio === ( exhortoRecibido?.idTipoJuicio ?? 0 ) }
                                        >
                                            { loadingTipoJuicio ? <CircularProgress size={ 20 } /> : <SaveIcon /> }
                                        </IconButton>

                                    </Box>
                                :
                                    <InfoField
                                        label="Tipo de juicio"
                                        value={ exhortoRecibido?.tipoJuicio }
                                        fontSize={ 16 }
                                    />
                            }

                        </Grid>
                }

                {
                    ( exhortoRecibido?.cuantia || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            {
                                edit
                                ?
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                        <TextField
                                            fullWidth
                                            label="Cuantía"
                                            value={ cuantia }
                                            onChange={ (e) => setCuantia( e.target.value ) }
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />

                                        <IconButton
                                            color='primary'
                                            onClick={ () => handleGuardarExhorto( 'cuantia' ) }
                                            disabled={ loadingCuantia || cuantia === ( exhortoRecibido?.cuantia ?? '' ) }
                                        >
                                            { loadingCuantia ? <CircularProgress size={ 20 } /> : <SaveIcon /> }
                                        </IconButton>

                                    </Box>
                                :
                                    exhortoRecibido?.cuantia
                                    &&
                                        <InfoField
                                            label="Cuantía"
                                            value={ formatCurrency( exhortoRecibido?.cuantia ) }
                                            fontSize={ 16 }
                                        />
                            }

                        </Grid>
                }

                {
                    ( exhortoRecibido?.autoridadExhortoRecibido && esPromocion )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Autoridad del exhorto recibido"
                                value={ exhortoRecibido?.autoridadExhortoRecibido }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( exhortoRecibido?.claseExhorto && esPromocion )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Clase del exhorto"
                                value={ exhortoRecibido?.claseExhorto }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    exhortoRecibido?.objetoExhorto
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Objeto del exhorto"
                                value={ exhortoRecibido?.objetoExhorto }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( exhortoRecibido?.etapa && exhortoRecibido?.subEtapa )
                    ?
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Etapa"
                                value={ `${ exhortoRecibido?.etapa } (${ exhortoRecibido?.subEtapa })` }
                                fontSize={ 16 }
                            />

                        </Grid>
                    :
                        exhortoRecibido?.etapa
                        &&
                            <Grid size={{ md: 6, xs: 12 }}>

                                <InfoField
                                    label="Etapa"
                                    value={ exhortoRecibido?.etapa }
                                    fontSize={ 16 }
                                />

                            </Grid>
                }

                {
                    exhortoRecibido?.juez
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Juez"
                                value={ exhortoRecibido?.juez }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    exhortoRecibido?.secretario
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Secretario(a) admisibilidad"
                                value={ exhortoRecibido?.secretario }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    exhortoRecibido?.juzgadoOrigen
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Juzgado de origen"
                                value={ exhortoRecibido?.juzgadoOrigen }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( actores.length !== 0 || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: "text.secondary",
                                                        letterSpacing: 0.5,
                                                        fontWeight: 700,
                                                    }}>
                                                        Actor(es):
                                                </Typography>

                                                {
                                                    edit
                                                    &&
                                                        <IconButton
                                                            size='small'
                                                            color='primary'
                                                            onClick={ () => setOpenDialogPartes( true ) }
                                                        >
                                                            <EditIcon fontSize='small' />
                                                        </IconButton>
                                                }

                                            </Box>
                                        }
                                        secondary={
                                            <List disablePadding>

                                                {
                                                    actores.map( (actor, index) => (

                                                        <ListItem key={ index } sx={{ py: 0, px: 0 }}>
                                                            <Typography sx={{ color: 'black' }} variant='subtitle2' fontWeight='bold' fontSize={ 16 }>{ actor }</Typography>
                                                        </ListItem>

                                                    ))
                                                }

                                            </List>
                                        }
                                        slotProps={{
                                            primary: { component: 'div' },
                                            secondary: { component: 'div' }
                                        }}
                                    />

                                </ListItem>

                            </List>

                        </Grid>
                }

                {
                    ( demandados.length !== 0 || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: "text.secondary",
                                                        letterSpacing: 0.5,
                                                        fontWeight: 700,
                                                    }}>
                                                        Demandado(s):
                                                </Typography>

                                                {
                                                    edit
                                                    &&
                                                        <IconButton
                                                            size='small'
                                                            color='primary'
                                                            onClick={ () => setOpenDialogPartes( true ) }
                                                        >
                                                            <EditIcon fontSize='small' />
                                                        </IconButton>
                                                }

                                            </Box>
                                        }
                                        secondary={
                                            <List disablePadding>

                                                {
                                                    demandados.map( (demandado, index) => (

                                                        <ListItem key={ index } sx={{ py: 0, px: 0 }}>
                                                            <Typography sx={{ color: 'black' }} variant='subtitle2' fontWeight='bold' fontSize={ 16 }>{ demandado }</Typography>
                                                        </ListItem>

                                                    ))
                                                }

                                            </List>
                                        }
                                        slotProps={{
                                            primary: { component: 'div' },
                                            secondary: { component: 'div' }
                                        }}
                                    />

                                </ListItem>

                            </List>

                        </Grid>
                }

                {
                    exhortoRecibido?.digitalizacion
                    &&
                        <Grid size={{ md: 12, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "text.secondary",
                                                    letterSpacing: 0.5,
                                                    fontWeight: 700,
                                                }}>
                                                    Digitalización:
                                            </Typography>
                                        }
                                    />

                                </ListItem>

                            </List>

                            <iframe
                                title='iframe-documento'
                                style={{ width: '100%', height: 800, borderRadius: 8 }}
                                src={ exhortoRecibido?.digitalizacion ?? '' }
                            />

                        </Grid>
                }

                {
                    exhortoRecibido?.acuseBuzonElectronico
                    &&
                        <Grid size={{ md: 12, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "text.secondary",
                                                    letterSpacing: 0.5,
                                                    fontWeight: 700,
                                                }}>
                                                    Acuse de envió buzón electronico:
                                            </Typography>
                                        }
                                    />

                                </ListItem>

                            </List>

                            <iframe
                                title='iframe-documento'
                                style={{ width: '100%', height: 800, borderRadius: 8 }}
                                src={ exhortoRecibido?.acuseBuzonElectronico ?? '' }
                            />

                        </Grid>
                }

            </Grid>

            <Dialog
                fullWidth
                maxWidth="md"
                open={ openAnexos }
                onClose={ handleCloseAnexos }
            >

                <DialogTitle>
                    Anexo(s) buzón electrónico
                </DialogTitle>

                <DialogContent sx={ DialogContentStyle }>

                    <List disablePadding>

                        {
                            exhortoRecibido?.anexosBuzonElectronico.map( (anexo, index) => (

                                <Box key={ index }>

                                    <ListItem sx={{ py: 2, px: 0 }}>

                                        <ListItemText
                                            primary={ <Typography variant='subtitle2' fontWeight='bold'>{ anexo.archivo }</Typography> }
                                            slotProps={{ primary: { component: 'div' }, secondary: { component: 'div' } }}
                                        />

                                        <Button
                                            variant='contained'
                                            size='small'
                                            onClick={ () => { setSelectedAnexoUrl( anexo.url ); setOpenDocumento( true ); } }
                                        >
                                            Ver
                                        </Button>

                                    </ListItem>

                                    {
                                        index < ( exhortoRecibido?.anexosBuzonElectronico.length ?? 0 ) - 1
                                        &&
                                            <Divider />
                                    }

                                </Box>

                            ))
                        }

                    </List>

                </DialogContent>

                <DialogActions sx={ DialogActionsStyle }>

                    <Button color='secondary' onClick={ handleCloseAnexos }>
                        Cerrar
                    </Button>

                </DialogActions>

            </Dialog>

            <DialogDocumento
                documento={ selectedAnexoUrl }
                openDialog={ openDocumento }
                handleCloseDialog={ () => setOpenDocumento( false ) }
            />

            <DialogPartesExhortoRecibido
                idExhortoRecibido={ exhortoRecibido?.idExhortoRecibido ?? 0 }
                openDialog={ openDialogPartes }
                handleCloseDialog={ () => setOpenDialogPartes( false ) }
                onGuardar={ ( a, d ) => {
                    setActores( a );
                    setDemandados( d );
                    setAcuerdo?.( prev => ({
                        ...prev,
                        exhortoRecibido: {
                            ...prev.exhortoRecibido,
                            actores: a,
                            demandados: d,
                        }
                    }));
                }}
            />

        </Box>
    )
}
